import axios from "axios";
import BaseService from "./baseService";
import fetchService from "./fetchService";
import authService from "./authService";
import { GetModulesResponse } from "./assessmentService";
import { artifactReviewCounts } from "utils/componentUtils";
import { UpdateBulkSubskillAssociationsInput, UpdateBulkSubskillLevelsInputArray } from "./educatorService.types";

class EducatorService extends BaseService {
  getLearners(
    schoolId: string,
    page?: number,
    limit?: number,
    sort?: string,
    externalId?: string,
    filter: string = "",
    mergedUnique?: any[],
  ) {
    this.servicePath = `schools/${schoolId}/learners/filtered`;
    const params: any = { page, limit, sort, filter };

    if (externalId) {
      params.section = externalId;
    }
    const body={
      learnerIds:mergedUnique
    }
    return fetchService.post<any>({
      url: this.getUrl(),
      data: mergedUnique&&mergedUnique?.length>0?body:{},
      params,
      needAuth: true,
    });
  }

  getAllActivitiesCounts(
    schoolId: string
  ) {
    this.servicePath = `schools/${schoolId}/educators/dashboard-widgets-activity-completion`;
    return fetchService.get<any>({
      url: this.getUrl(),
      needAuth: true,
    });
  }

  async evidenceCount(learner_ids: any) {
    const config = {
      method: "POST",
      url: `${this.getProgressUrl()}/api/v1/learners/evidence-count/`,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
        "Content-Type": "application/json",
      },
      data: { learner_ids },
      withCredentials: true, // <-- Important for cross-origin requests (if applicable)
    };

    try {
      const response = await axios(config);
      return response.data || {};
    } catch (error: any) {
      console.error("Error in evidence count:", error.response || error);
      return error.response
    }
  }

  async getCompetencyModel (schoolId: string) {
    const config = {
      method: "GET",
      url: `${this.getProgressUrl()}/api/v1/schools/${schoolId}/mca-models/`,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
        "Content-Type": "application/json",
      },
    }

    try {
      const response = await axios(config);
      return response.data || {};
    } catch (error: any) {
      console.error("Error getting competency model", error.response || error);
      return error.response;
    }
  }

  async getArtifactdetails(schoolId: string, eduId: string, learnerIds: any) {
    const config = {
      method: "POST",
      url: `${this.getAwsBaseUrl()}/schools/${schoolId}/educators/${eduId}/learner-stats`,
      headers: {
        Authorization: `Bearer ${authService.getIdToken()}`,
        "Content-Type": "application/json",
      },
      data: { learnerIds },
      withCredentials: false, // <-- Important for cross-origin requests (if applicable)
    };

    try {
      const response = await axios(config);
      return response.data || {};
    } catch (error: any) {
      console.error("Error in evidence count:", error.response || error);
      return error.response
    }
  }
  

  async educatorArtifacts(studentIds: any) {
    const config = {
      method: "POST",
      url: `${this.getProgressUrl()}/api/v1/learners/dashboard-widget-artifact/?page=1&limit=10`,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
        "Content-Type": "application/json",
      },
      data: { studentIds },
      withCredentials: true, // <-- Important for cross-origin requests (if applicable)
    };

    try {
      const response = await axios(config);
      return response.data || {};
    } catch (error: any) {
      console.error("Error uploading artifacts:", error.response || error);
      return error.response
    }
  }

  getLearnerGroups(
    schoolId: string,
    page?: number,
    limit?: number,
    sort?: string,
    externalId?: string,
    filter: string = "",
  ) {
    this.servicePath = `section/${schoolId}`;
    return fetchService.get<any>({
      url: this.getUrl(),
      needAuth: true,
    });
  }

  async getLearnerSubs(emails: any) {
    const config = {
      method: "POST",
      url: `${this.getAwsBaseUrl()}/listUsers`,
      data: { body: JSON.stringify(emails) },
      headers: {
        Authorization: `Bearer ${authService.getIdToken()}`,
        "Content-Type": "application/json",
      },
      withCredentials: false,
    };

    try {
      const response = await axios(config);
      return JSON.parse(response.data.body);
    } catch (error: any) {
      console.error("Error fetching learner subs:", error.response || error);
      throw error;
    }
  }

  async getArtifactCounts(
    learnerId: number,
    page: number = 1,
    limit: number = 10
  ) {
    const config = {
      method: "GET",
      url: `${this.getProgressUrl()}/api/v1/learners/${learnerId}/evidences/?page=${page}&limit=${limit}`,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
        "Content-Type": "application/json",
      },
      withCredentials: false,
    };
    try {
      const response = await axios(config);
      const reviewCount = artifactReviewCounts(response.data)
      return {
        reviewCount,
        artifactCount: response?.data?.count?.total ?? 0,
      };
    } catch (error: any) {
      console.error("Error fetching learner subs:", error.response || error);
      throw error;
    }
  }

  async getArtifacts(learnerId: number, page: number = 1, limit: number = 10) {
    const config = {
      method: "GET",
      url: `${this.getProgressUrl()}/api/v1/learners/${learnerId}/evidences/?page=${page}&limit=${limit}`,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
        "Content-Type": "application/json",
      },
      withCredentials: true,
    };
    try {
      const response = await axios(config);
      return response.data || {};
    } catch (error: any) {
      console.error("Error fetching learner subs:", error.response || error);
      throw error;
    }
  }

  async uploadArtifacts(data: any) {
    const config = {
      method: "POST",
      url: `${this.getProgressUrl()}/api/v1/evidences/upload/`,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
        cognitoauthorization: `${authService.getIdToken()}`,
      },
      data: data,
      withCredentials: true, // <-- Important for cross-origin requests (if applicable)
    };

    try {
      const response = await axios(config);
      return response.data || {};
    } catch (error: any) {
      console.error("Error uploading artifacts:", error.response || error);
      return error.response
    }
  }

  async fetchAssociatedSkills(schoolId: string, isPlanningLibrary: boolean = false) {
    let url = `${this.getBaseUrl()}/schools/${schoolId}/mca?includeArchived=true&includeArchivedSteps=true`;

    if (isPlanningLibrary) {
      url += '&isPlanningLibrary=true';
    }
    const config = {
      method: "GET",
      url,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
      },
      withCredentials: false, // <-- Important for cross-origin requests (if applicable)
    };

    try {
      const response = await axios(config);
      return response.data.data || {};
    } catch (error: any) {
      console.error("Error uploading artifacts:", error.response || error);
      throw error;
    }
  }

  async assiociateSkill(learnerId: number, creditId: any) {
    const config = {
      method: "GET",
      url: `${this.getProgressUrl()}/api/v1/credits/${creditId}/learners/${learnerId}/evidences/`,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
      },
      withCredentials: true,
    };

    try {
      const response = await axios(config);
      if (response.status == 200) {
        return true
      }
      return false
    } catch (error: any) {
      console.error("Error fetching data:", error.response || error);
      throw error;
    }
  }

  async saveAssociatedSkill(learnerId: any, subSkillId: any, skillId: any) {
    //learner-credit-progress/evidence/6/credits/1690/learners/111/'
    const config = {
      method: "POST",
      url: `${this.getProgressUrl()}/api/v1/learner-credit-progress/evidence/${skillId}/credits/${subSkillId}/learners/${learnerId}/`,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
      },
      withCredentials: true,
    };

    try {
      const response = await axios(config);
      return response.data || {};
    } catch (error: any) {
      console.error("Error fetching data:", error.response || error);
      return error.response.data;
    }
  }

  async updateBulkSubskillAssociations({ artifactId, learnerId, requestBody }: UpdateBulkSubskillAssociationsInput) {
    const config = {
      method: "POST",
      url: `${this.getProgressUrl()}/api/v1/learner-credit-progress/evidence/${artifactId}/learners/${learnerId}/bulk-associate/`,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
      },
      data: requestBody,
    }

    try {
      const response = await axios(config);
      return response.data || {};
    } catch (error: any) {
      console.error("Error updating bulk subskill associations:", error.response || error);
      return error.response.data;
    }
  }

  async updateBulkSubskillLevels(requestBody: UpdateBulkSubskillLevelsInputArray) {
    const config = {
      method: "POST",
      url: `${this.getProgressUrl()}/api/v1/learner-credit-progress/bulk-evidence-levels/`,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
      },
      data: requestBody,
    }

    try {
      const response = await axios(config);
      return response.data || {};
    } catch (error: any) {
      console.error("Error updating bulk subskill levels: ", error.response || error);
      return error.response.data;
    }
  }

  async getEvidenceDetails(evidenceId: any) {
    const config = {
      method: "GET",
      url: `${this.getProgressUrl()}/api/v1/evidences/${evidenceId}/`,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
      },
      withCredentials: true,
    };

    try {
      const response = await axios(config);
      return response.data || {};
    } catch (error: any) {
      console.error("Error fetching data:", error.response || error);
      throw error;
    }
  }

  async updateEvidence(evidenceId: any, data: any) {
    const config = {
      method: "PATCH",
      url: `${this.getProgressUrl()}/api/v1/evidences/${evidenceId}/`,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
        cognitoauthorization: `${authService.getIdToken()}`,
      },
      data: data,
      withCredentials: true,
    };

    try {
      const response = await axios(config);
      return response.data || {};
    } catch (error: any) {
      console.error("Error fetching data:", error.response || error);
      throw error;
    }
  }

  async deleteEvidence(evidenceId: any) {
    const config = {
      method: "DELETE",
      url: `${this.getProgressUrl()}/api/v1/evidences/${evidenceId}/`,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
      },
      withCredentials: true,
    };

    try {
      const response = await axios(config);
      return response.data || {};
    } catch (error: any) {
      console.error("Error fetching data:", error.response || error);
      throw error;
    }
  }

  async addComment(object_id: number, text: string, content_type: number = 14, learning_level_number: number = 1) {
    const config = {
      method: "POST",
      url: `${this.getProgressUrl()}/api/v1/comment/`,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
        cognitoauthorization: `${authService.getIdToken()}`,
      },
      data: {
        text, content_type, object_id, learning_level_number
      },
      withCredentials: true,
    };

    try {
      const response = await axios(config);
      return response.data || {};
    } catch (error: any) {
      console.error("Error fetching data:", error.response || error);
      throw error;
    }
  }

  async resetModules(externalRequestId: string,
    requestingUserId: string,
    studentGuids: string[],
    modules: string[],
    type: string) {
    const url = `${this.getBaseUrl()}/assessment-modules/reset-modules`; // Add query params here if needed

    const data = {
      externalRequestId,
      requestingUserId,
      type,
      data: {
        studentGuids,
        modules,
      },
    };

    const config = {
      method: "POST",
      url,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
        "Content-Type": "application/json",
      },
      data,
      withCredentials: false, // Set to true if your API requires sending cookies
    };

    try {
      const response = await axios(config);
      return response.data; // Adjust if response shape differs
    } catch (error: any) {
      console.error("Error posting student modules:", error.response || error);
      throw error;
    }
  }

  async deleteAssociatedSkill(evidenceProgressId: any) {
    const config = {
      method: "DELETE",
      url: `${this.getProgressUrl()}/api/v1/learner-credit-progress/evidence_progress/${evidenceProgressId}/`,
      headers: {
        Authorization: `${authService.getAuthToken()}`,
      },
      withCredentials: true,
    };

    try {
      const response = await axios(config);
      return response.data || {};
    } catch (error: any) {
      console.error("Error fetching data:", error.response || error);
      throw error;
    }
  }
}

const educatorService = new EducatorService();
export default educatorService;
